import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_TRAILER_TYPE_SLUG } from "../../supabase/functions/_shared/models/trailer/trailer-type.ts";

/**
 * Not a discriminated union on purpose: this project compiles with
 * `strictNullChecks: false`, which disables literal-type narrowing.
 */
export interface EnsureTrailerResult {
  ok: boolean;
  created?: boolean;
  reason?: "permission_denied" | "insert_failed";
  message?: string;
}

/**
 * Ensures a trailer record exists in the trailers table when a trailer_id is assigned to an order.
 * If the trailer doesn't exist, creates one with the order's trailer type and departure location.
 * Does NOT create a trailer event.
 *
 * Returns a typed result so callers can distinguish a permissions failure from a
 * generic insert failure. Existing callers that ignore the return value keep their
 * previous behaviour (errors are still logged, never thrown).
 */
// TWIN: supabase/functions/trailer-arrival (rules.ts / record-arrival.ts)
// creates unknown trailers for EAPI arrival reports with the same rules
// (trimmed number, 48_dry_van type fallback, 'unknown' location status when
// no location). Keep the two in step.
export async function ensureTrailerExists({
  trailerId,
  trailerTypeId,
  shipFromCity,
  tenantId,
}: {
  trailerId: string;
  trailerTypeId?: string | null;
  shipFromCity?: string | null;
  tenantId?: string | null;
}): Promise<EnsureTrailerResult> {
  if (!trailerId.trim()) return { ok: true, created: false };

  // Check if trailer already exists
  const existsBase = supabase
    .from("trailers")
    .select("id")
    .eq("trailer_id", trailerId.trim());
  const { data: existing } = await (tenantId
    ? existsBase.eq("tenant_id", tenantId)
    : existsBase
  ).maybeSingle();

  if (existing) return { ok: true, created: false }; // Already exists, do nothing

  // Find the location matching the ship_from_city (tenant-scoped)
  let locationId: string | null = null;
  if (shipFromCity) {
    let locQuery: any = supabase
      .from("locations")
      .select("id")
      .eq("archived", false)
      .eq("city", shipFromCity);
    if (tenantId) locQuery = locQuery.eq("tenant_id", tenantId);
    const { data: location } = await locQuery.maybeSingle();
    locationId = (location as { id: string } | null)?.id ?? null;
  }

  // If no trailer_type_id provided, default to the first trailer type (48' Dry Van)
  let resolvedTypeId = trailerTypeId || null;
  if (!resolvedTypeId) {
    // `trailer_types` is a global catalog with no `tenant_id` column — adding a
    // tenant filter here makes the request 400 and silently leaves the new
    // trailer with no type, which then reads as "missing trailer type" in the
    // order forms. Do not re-add it.
    const { data: defaultType } = await supabase
      .from("trailer_types")
      .select("id")
      .eq("slug", DEFAULT_TRAILER_TYPE_SLUG)
      .maybeSingle();
    resolvedTypeId = (defaultType as { id: string } | null)?.id ?? null;
  }

  const insertPayload: any = {
    trailer_id: trailerId.trim(),
    trailer_type_id: resolvedTypeId,
    current_location_id: locationId,
    current_location_status: locationId ? "at_site" : "unknown",
  };
  if (tenantId) insertPayload.tenant_id = tenantId;

  const { error } = await supabase.from("trailers").insert(insertPayload);

  if (error) {
    console.error("Failed to auto-create trailer:", error);
    const code = (error as { code?: string }).code;
    const isPermission =
      code === "42501" ||
      /row-level security|permission denied/i.test(error.message ?? "");
    return {
      ok: false,
      reason: isPermission ? "permission_denied" : "insert_failed",
      message: error.message ?? "Unknown error",
    };
  }

  return { ok: true, created: true };
}
